
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import FinanceiroHeader from '@/components/financeiro/FinanceiroHeader';
import FinanceiroTabs from "@/components/financeiro/FinanceiroTabs";
import ClientesTable from '@/components/financeiro/ClientesTable';
import ResumoCards from '@/components/financeiro/ResumoCards';
import { useFinanceiroGerador } from '@/hooks/useFinanceiroGerador';
import { exportToCSV } from '@/utils/pdfExport';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Toaster } from "sonner";

const FinanceiroGeradorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [search, setSearch] = useState('');
  const [tabAtiva, setTabAtiva] = useState("contas");
  
  const { 
    gerador, 
    clientes, 
    resumo, 
    editingTarifas,
    originalTarifas,
    editingTarifasGerador,
    originalTarifasGerador,
    handleTarifaChange,
    handleSaveTarifa,
    handleUndoTarifa,
    handleTarifaGeradorChange,
    handleSaveTarifaGerador,
    handleUndoTarifaGerador,
    handleExport
  } = useFinanceiroGerador(id);

  const handleTabChange = (value: string) => {
    setTabAtiva(value);
    
    const event = new CustomEvent('change-tab', {
      detail: { tab: value }
    });
    window.dispatchEvent(event);
    
    const url = new URL(window.location.href);
    url.searchParams.set('tab', value);
    window.history.pushState({}, '', url);
  };
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) {
      setTabAtiva(tab);
    }
  }, []);

  const handleGoBack = () => {
    navigate('/financeiro');
  };

  if (!gerador) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <h2 className="text-xl font-normal text-gray-700">Carregando dados do gerador...</h2>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-4 md:p-6 max-w-7xl">
        <div className="flex flex-col mb-6">
          <div className="mb-4">
            <Button variant="ghost" onClick={handleGoBack} className="mb-4 text-gray-600 hover:text-gray-800 -ml-3 group">
              <ArrowLeft className="h-4 w-4 mr-2 text-blue-400 group-hover:text-blue-500 transition-colors" />
              Voltar para dashboard
            </Button>
            <h1 className="text-2xl font-normal text-gray-700 mb-1">Financeiro: {gerador.nomeTitular}</h1>
            <p className="text-sm text-gray-500 mt-1 opacity-75">
              Gerencie os clientes vinculados ao gerador e suas tarifas
            </p>
          </div>
          <div className="w-full bg-white p-4 rounded-lg shadow-sm">
            <FinanceiroTabs 
              tabAtiva={tabAtiva} 
              onChangeTab={handleTabChange} 
              variant="buttons"
              className="overflow-x-auto pb-2 md:pb-0" 
            />
          </div>
        </div>
        
        <div className="mb-6">
          <FinanceiroHeader 
            titulo={`Gerenciamento de Clientes`}
            geradorNome={`Gerenciamento de Clientes: ${gerador.nomeTitular}`}
            search={search} 
            onSearchChange={setSearch} 
            onExport={handleExport} 
            hasClientes={clientes.length > 0}
          />
        </div>
        
        <Tabs 
          defaultValue="contas" 
          className="w-full" 
          value={tabAtiva} 
          onValueChange={handleTabChange}
        >
          <TabsContent value="contas" className="animate-in fade-in-50">
            {clientes.length > 0 ? (
              <>
                <Card className="mb-6 shadow-sm border-gray-100 hover:shadow-md transition-shadow">
                  <CardContent className="p-0">
                    <ResumoCards resumo={resumo} />
                  </CardContent>
                </Card>
                
                <Card className="shadow-sm border-gray-100 mb-6 overflow-hidden">
                  <CardContent className="p-0">
                    <ClientesTable 
                      clientes={clientes}
                      gerador={gerador}
                      editingTarifas={editingTarifas}
                      originalTarifas={originalTarifas}
                      editingTarifasGerador={editingTarifasGerador}
                      originalTarifasGerador={originalTarifasGerador}
                      onTarifaChange={handleTarifaChange}
                      onSaveTarifa={handleSaveTarifa}
                      onUndoTarifa={handleUndoTarifa}
                      onTarifaGeradorChange={handleTarifaGeradorChange}
                      onSaveTarifaGerador={handleSaveTarifaGerador}
                      onUndoTarifaGerador={handleUndoTarifaGerador}
                    />
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="shadow-sm border-gray-100">
                <CardContent className="p-6 text-center">
                  <p className="text-gray-500 mb-4">Nenhum cliente encontrado para este gerador.</p>
                  <Button 
                    onClick={() => navigate('/clientes/novo')} 
                    className="bg-blue-500 hover:bg-blue-600 text-white shadow-sm"
                  >
                    Adicionar cliente
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        <Toaster position="top-right" />
      </div>
    </Layout>
  );
};

export default FinanceiroGeradorPage;
